/**
 * DNA (Did Not Attend) email dispatch — Settings card.
 *
 * When a client's appointment carries the configured "DNA" Acuity label,
 * a follow-up email is sent from the app. Two variants:
 *  - "Another appointment"  — when the client has a future non-cancelled booking
 *  - "No further"           — when the client has no upcoming bookings
 *
 * Business variables (phone, email, website, booking links) are configured
 * here and injected into both templates as {{Placeholders}}.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { CalendarX, Save, Play, Send, RefreshCw, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const PLACEHOLDER_HINTS = [
  "{{FirstName}}", "{{LastName}}", "{{FullName}}",
  "{{AppointmentDate}}", "{{AppointmentTime}}", "{{Practitioner}}",
  "{{NextAppointmentDate}}", "{{NextAppointmentTime}}", "{{NextPractitioner}}",
  "{{OnlineBookingLink}}",
  "{{BusinessPhone}}", "{{BusinessEmail}}", "{{BusinessWebsite}}",
];

export default function DnaDispatchSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [labels, setLabels] = useState([]);
  const [labelsErr, setLabelsErr] = useState("");
  const [types, setTypes] = useState([]);
  const [log, setLog] = useState([]);

  // Form state
  const [enabled, setEnabled] = useState(false);
  const [dnaLabelId, setDnaLabelId] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [lookBackDays, setLookBackDays] = useState(3);
  const [phone, setPhone] = useState("");
  const [emailAddr, setEmailAddr] = useState("");
  const [website, setWebsite] = useState("");
  const [defaultBooking, setDefaultBooking] = useState("");
  const [bookingByType, setBookingByType] = useState([]); // [{appointment_type_id, url}]
  const [anotherSubject, setAnotherSubject] = useState("");
  const [anotherHtml, setAnotherHtml] = useState("");
  const [noFurtherSubject, setNoFurtherSubject] = useState("");
  const [noFurtherHtml, setNoFurtherHtml] = useState("");
  const [previewKind, setPreviewKind] = useState("no_further");
  const [previewTo, setPreviewTo] = useState("");

  async function refresh() {
    setLoading(true);
    try {
      const c = await api.get("/dna/config");
      const d = c.data || {};
      setEnabled(!!d.enabled);
      setDnaLabelId(d.dna_label_id ? String(d.dna_label_id) : "");
      setSenderEmail(d.sender_email || "");
      setLookBackDays(d.look_back_days ?? 3);
      setPhone(d.business_phone || "");
      setEmailAddr(d.business_email || "");
      setWebsite(d.business_website || "");
      setDefaultBooking(d.default_booking_link || "");
      setBookingByType(d.booking_links_by_type || []);
      setAnotherSubject(d.another_subject || "");
      setAnotherHtml(d.another_html || "");
      setNoFurtherSubject(d.no_further_subject || "");
      setNoFurtherHtml(d.no_further_html || "");
    } catch (e) {
      toast.error("Could not load DNA config", {
        description: e?.response?.data?.detail || e.message,
      });
    }
    try {
      const r = await api.get("/labels");
      setLabels(r.data || []);
      setLabelsErr("");
    } catch (e) {
      setLabels([]);
      setLabelsErr(e?.response?.data?.detail || "Acuity labels unavailable");
    }
    try {
      const t = await api.get("/appointment-types");
      setTypes(t.data || []);
    } catch {
      setTypes([]);
    }
    try {
      const l = await api.get("/dna/log?limit=25");
      setLog(l.data?.rows || []);
    } catch {
      setLog([]);
    }
    setLoading(false);
  }

  useEffect(() => { refresh(); }, []);

  const typeNameById = useMemo(() => {
    const m = {};
    for (const t of types || []) m[String(t.id)] = t.name;
    return m;
  }, [types]);

  async function save() {
    setSaving(true);
    try {
      const payload = {
        enabled,
        dna_label_id: dnaLabelId ? Number(dnaLabelId) : null,
        sender_email: senderEmail || null,
        look_back_days: Number(lookBackDays) || 3,
        business_phone: phone,
        business_email: emailAddr,
        business_website: website,
        default_booking_link: defaultBooking,
        booking_links_by_type: bookingByType
          .filter((b) => b.appointment_type_id && b.url)
          .map((b) => ({ appointment_type_id: Number(b.appointment_type_id), url: b.url })),
        another_subject: anotherSubject,
        another_html: anotherHtml,
        no_further_subject: noFurtherSubject,
        no_further_html: noFurtherHtml,
      };
      await api.post("/dna/config", payload);
      toast.success("DNA email settings saved");
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  async function runNow() {
    setRunning(true);
    try {
      const r = await api.post("/dna/run-now");
      const d = r.data || {};
      if (d.skipped) {
        toast.warning(`Skipped: ${d.skipped}`);
      } else {
        toast.success(`Checked ${d.checked} DNA appointment(s) · sent ${d.sent} · already sent ${d.already_sent} · no email ${d.no_email}`);
      }
      // Refresh log
      const l = await api.get("/dna/log?limit=25");
      setLog(l.data?.rows || []);
    } catch (e) {
      toast.error("Run failed", { description: e?.response?.data?.detail || e.message });
    } finally { setRunning(false); }
  }

  async function preview() {
    if (!previewTo) { toast.error("Enter a preview email address"); return; }
    setPreviewing(true);
    try {
      await api.post("/dna/preview", {
        kind: previewKind, to_email: previewTo, first_name: "Sample",
      });
      toast.success(`Preview sent to ${previewTo}`);
    } catch (e) {
      toast.error("Preview failed", { description: e?.response?.data?.detail || e.message });
    } finally { setPreviewing(false); }
  }

  function addBookingOverride() {
    setBookingByType([...bookingByType, { appointment_type_id: "", url: "" }]);
  }
  function updateBookingOverride(i, patch) {
    const next = bookingByType.slice();
    next[i] = { ...next[i], ...patch };
    setBookingByType(next);
  }
  function removeBookingOverride(i) {
    setBookingByType(bookingByType.filter((_, idx) => idx !== i));
  }

  return (
    <Card className="p-6 space-y-6" data-testid="dna-dispatch-settings">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-zinc-900">
            <CalendarX className="h-5 w-5" /> DNA follow-up emails
          </h2>
          <p className="text-sm text-zinc-600 mt-1 max-w-2xl">
            When staff apply the &quot;DNA&quot; label to an appointment in Acuity, the app
            automatically emails that client. If they&apos;ve already got another
            appointment booked, they receive the &quot;another appointment&quot; email;
            otherwise they receive the &quot;no further&quot; email with a booking link.
            Runs hourly at :20 and once at 18:30 as a safety net.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Label htmlFor="dna-enabled" className="text-sm">Enabled</Label>
          <Switch id="dna-enabled" checked={enabled}
            onCheckedChange={setEnabled} data-testid="dna-enabled" />
        </div>
      </div>

      {/* Triggers */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="space-y-1">
          <Label>DNA Acuity label</Label>
          <Select value={dnaLabelId || "__none__"}
            onValueChange={(v) => setDnaLabelId(v === "__none__" ? "" : v)}>
            <SelectTrigger data-testid="dna-label-select">
              <SelectValue placeholder="Choose label" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— None —</SelectItem>
              {labels.map((l) => (
                <SelectItem key={l.id} value={String(l.id)}>
                  {l.name || `Label #${l.id}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {labelsErr && (
            <p className="text-2xs text-amber-700">{labelsErr}</p>
          )}
        </div>
        <div className="space-y-1">
          <Label>Look-back window (days)</Label>
          <Input type="number" min={1} max={14} value={lookBackDays}
            onChange={(e) => setLookBackDays(e.target.value)}
            data-testid="dna-lookback" />
          <p className="text-2xs text-zinc-500">
            Each tick scans this many days back for un-emailed DNAs.
          </p>
        </div>
        <div className="space-y-1">
          <Label>Sender email (optional override)</Label>
          <Input type="email" value={senderEmail}
            onChange={(e) => setSenderEmail(e.target.value)}
            placeholder="Uses the daily-schedule sender by default"
            data-testid="dna-sender" />
        </div>
      </div>

      {/* Business variables */}
      <div className="rounded-lg border border-zinc-200 p-4 space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900">Business variables</h3>
          <p className="text-2xs text-zinc-500">
            Available in every template as
            {" "}<code className="text-zinc-800">{"{{BusinessPhone}}"}</code>,
            {" "}<code className="text-zinc-800">{"{{BusinessEmail}}"}</code>,
            {" "}<code className="text-zinc-800">{"{{BusinessWebsite}}"}</code>.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label>Phone</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="(03) 9455 2998" data-testid="dna-phone" />
          </div>
          <div className="space-y-1">
            <Label>Email</Label>
            <Input value={emailAddr} onChange={(e) => setEmailAddr(e.target.value)}
              placeholder="info@lebbrehab.com.au" data-testid="dna-email" />
          </div>
          <div className="space-y-1">
            <Label>Website</Label>
            <Input value={website} onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://lebbrehab.com.au" data-testid="dna-website" />
          </div>
        </div>
      </div>

      {/* Booking links */}
      <div className="rounded-lg border border-zinc-200 p-4 space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900">Booking links</h3>
          <p className="text-2xs text-zinc-500">
            The &quot;no further&quot; template renders <code>{"{{OnlineBookingLink}}"}</code>.
            Per-appointment-type overrides win; otherwise the default is used.
          </p>
        </div>
        <div className="space-y-1">
          <Label>Default booking link</Label>
          <Input value={defaultBooking} onChange={(e) => setDefaultBooking(e.target.value)}
            placeholder="https://app.acuityscheduling.com/schedule.php?owner=..."
            data-testid="dna-default-booking" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wide text-zinc-500">
            Per-appointment-type overrides
          </Label>
          {bookingByType.length === 0 && (
            <p className="text-2xs text-zinc-500">
              No overrides — every DNA appointment will use the default booking link.
            </p>
          )}
          {bookingByType.map((b, i) => (
            <div key={i} className="grid grid-cols-[1fr_2fr_36px] gap-2"
              data-testid={`dna-booking-override-${i}`}>
              <Select value={String(b.appointment_type_id) || "__pick__"}
                onValueChange={(v) => updateBookingOverride(i, { appointment_type_id: v === "__pick__" ? "" : v })}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Appointment type" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__pick__">— Pick type —</SelectItem>
                  {types.map((t) => (
                    <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input value={b.url}
                onChange={(e) => updateBookingOverride(i, { url: e.target.value })}
                placeholder="https://..." className="h-9" />
              <Button variant="outline" size="icon" className="h-9 w-9"
                onClick={() => removeBookingOverride(i)}
                data-testid={`dna-booking-remove-${i}`}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addBookingOverride}
            data-testid="dna-booking-add">
            <Plus className="h-3 w-3 mr-1" />Add override
          </Button>
        </div>
      </div>

      {/* Email templates */}
      <div className="rounded-lg border border-zinc-200 p-4 space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-zinc-900">Email templates</h3>
            <p className="text-2xs text-zinc-500">
              Placeholders: {PLACEHOLDER_HINTS.map((p) => (
                <code key={p} className="mx-0.5 text-zinc-700">{p}</code>
              ))}
            </p>
          </div>
        </div>

        <Tabs defaultValue="no_further">
          <TabsList>
            <TabsTrigger value="no_further" data-testid="dna-tab-no-further">
              No further appointments
            </TabsTrigger>
            <TabsTrigger value="another" data-testid="dna-tab-another">
              Client has another appointment
            </TabsTrigger>
          </TabsList>

          <TabsContent value="no_further" className="space-y-3 mt-3">
            <div className="space-y-1">
              <Label>Subject</Label>
              <Input value={noFurtherSubject}
                onChange={(e) => setNoFurtherSubject(e.target.value)}
                data-testid="dna-nofurther-subject" />
            </div>
            <div className="space-y-1">
              <Label>Body (HTML)</Label>
              <Textarea rows={12} value={noFurtherHtml}
                onChange={(e) => setNoFurtherHtml(e.target.value)}
                className="font-mono text-xs"
                data-testid="dna-nofurther-html" />
            </div>
          </TabsContent>

          <TabsContent value="another" className="space-y-3 mt-3">
            <div className="space-y-1">
              <Label>Subject</Label>
              <Input value={anotherSubject}
                onChange={(e) => setAnotherSubject(e.target.value)}
                data-testid="dna-another-subject" />
            </div>
            <div className="space-y-1">
              <Label>Body (HTML)</Label>
              <Textarea rows={12} value={anotherHtml}
                onChange={(e) => setAnotherHtml(e.target.value)}
                className="font-mono text-xs"
                data-testid="dna-another-html" />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Preview to</Label>
            <div className="flex items-center gap-2">
              <Select value={previewKind} onValueChange={setPreviewKind}>
                <SelectTrigger className="w-56 h-9" data-testid="dna-preview-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no_further">No further appointments</SelectItem>
                  <SelectItem value="another_appointment">Another appointment</SelectItem>
                </SelectContent>
              </Select>
              <Input type="email" value={previewTo}
                onChange={(e) => setPreviewTo(e.target.value)}
                placeholder="you@example.com"
                className="w-64 h-9" data-testid="dna-preview-to" />
              <Button variant="outline" onClick={preview} disabled={previewing}
                data-testid="dna-preview-send">
                {previewing ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
                Send preview
              </Button>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={runNow} disabled={running}
            data-testid="dna-run-now">
            {running ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : <Play className="h-3 w-3 mr-1" />}
            Run now
          </Button>
          <Button onClick={save} disabled={saving} data-testid="dna-save">
            {saving ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
            Save
          </Button>
        </div>
      </div>

      {/* Dispatch log */}
      <div className="rounded-lg border border-zinc-200 overflow-hidden">
        <div className="flex items-center justify-between p-3 border-b border-zinc-200">
          <h3 className="text-sm font-semibold text-zinc-900">Recent dispatches</h3>
          <span className="text-2xs text-zinc-500">
            {loading ? "Loading…" : `${log.length} shown`}
          </span>
        </div>
        <div className="max-h-72 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr>
                <th className="text-left px-3 py-2">Sent at</th>
                <th className="text-left px-3 py-2">Client</th>
                <th className="text-left px-3 py-2">Missed appt</th>
                <th className="text-left px-3 py-2">Variant</th>
                <th className="text-left px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {log.length === 0 ? (
                <tr><td colSpan={5} className="px-3 py-4 text-center text-zinc-500">
                  No dispatches yet.
                </td></tr>
              ) : log.map((row) => (
                <tr key={row.missed_appointment_id} className="border-t border-zinc-100"
                  data-testid={`dna-log-row-${row.missed_appointment_id}`}>
                  <td className="px-3 py-1.5 font-mono text-2xs">
                    {row.sent_at ? new Date(row.sent_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-3 py-1.5">
                    {row.client_first_name || row.client_last_name
                      ? `${row.client_first_name || ""} ${row.client_last_name || ""}`.trim()
                      : "—"}
                    <span className="ml-1 text-zinc-500">· {row.to}</span>
                  </td>
                  <td className="px-3 py-1.5 font-mono text-2xs">
                    {row.missed_datetime ? new Date(row.missed_datetime).toLocaleString() : "—"}
                  </td>
                  <td className="px-3 py-1.5">
                    {row.variant === "another_appointment" ? "Another appt" : "No further"}
                  </td>
                  <td className="px-3 py-1.5">
                    <span className={
                      row.status === "SUCCESS" ? "text-emerald-700" :
                      row.status === "FAILED" ? "text-red-700" : "text-zinc-600"
                    }>
                      {row.status || "—"}
                    </span>
                    {row.error && (
                      <div className="text-2xs text-red-700 truncate max-w-xs" title={row.error}>
                        {row.error}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
}
